import { useIsMutating, useMutation } from '@tanstack/react-query';
import { chatbotApiClient } from 'src/api/FiretrolApiClientInstances';
import { queryKeys } from 'src/api/queries/queryKeys';

const ROW_LIMIT_HEADER = 'x-row-limit-reached';

export interface IExportChatQueryCsvArgs {
  conversationId: string;
  auditId: string;
}

export interface IChatQueryCsvFile {
  data: Blob;
  /** The row cap the server applied; null when every row fit in the file. */
  rowLimit: number | null;
}

export const rowLimitFromHeaders = (
  headers: Record<string, unknown> | undefined,
): number | null => {
  const entry = Object.entries(headers ?? {}).find(
    ([name]) => name.toLowerCase() === ROW_LIMIT_HEADER,
  );
  if (!entry) return null;
  const limit = Number(entry[1]);
  return Number.isInteger(limit) && limit > 0 ? limit : null;
};

/** @registry Re-runs a chat reply's saved query under the caller's current access and resolves the CSV blob plus the row cap when the server cut rows off; failures toast through the global error pipeline. */
export function useExportChatQueryCsv({
  conversationId,
  auditId,
}: IExportChatQueryCsvArgs) {
  return useMutation({
    mutationKey: queryKeys.chat.queryExport(auditId),
    mutationFn: async (): Promise<IChatQueryCsvFile | null> => {
      const file = await chatbotApiClient.exportQueryCsv(
        conversationId,
        auditId,
      );
      return file
        ? { data: file.data, rowLimit: rowLimitFromHeaders(file.headers) }
        : null;
    },
    meta: { errorContext: 'chat query export' },
  });
}

/** True while an export of this query runs, including one started by a component that has since unmounted. */
export const useIsExportingChatQueryCsv = (auditId: string): boolean =>
  useIsMutating({ mutationKey: queryKeys.chat.queryExport(auditId) }) > 0;
